import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CHART_AXIS,
  CHART_COLORS,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chart-theme';

export interface StackedSeries<T> {
  key: keyof T & string;
  label: string;
  color?: string;
}

interface StackedBarChartProps<T extends { date: string }> {
  data: T[];
  series: StackedSeries<T>[];
  height?: number;
  xFormatter?: (raw: string) => string;
}

const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'] as const;
function defaultFmt(raw: string): string {
  const [, m, d] = raw.split('-');
  if (!m || !d) return raw;
  const idx = Math.max(0, Math.min(11, Number(m) - 1));
  return `${months[idx]} ${Number(d)}`;
}

export function StackedBarChart<T extends { date: string }>({
  data,
  series,
  height = 280,
  xFormatter = defaultFmt,
}: StackedBarChartProps<T>): JSX.Element {
  const resolved = useMemo(
    () =>
      series.map((s, i) => ({
        ...s,
        color: s.color ?? CHART_COLORS[i % CHART_COLORS.length],
      })),
    [series],
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
        <CartesianGrid stroke={CHART_AXIS.stroke} vertical={false} />
        <XAxis
          dataKey="date"
          stroke={CHART_AXIS.stroke}
          tick={CHART_AXIS.tick}
          tickLine={false}
          tickFormatter={xFormatter}
          minTickGap={28}
        />
        <YAxis
          stroke={CHART_AXIS.stroke}
          tick={CHART_AXIS.tick}
          tickLine={false}
          axisLine={false}
          width={48}
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={CHART_TOOLTIP_STYLE}
          itemStyle={CHART_TOOLTIP_ITEM_STYLE}
          labelStyle={{ color: '#CCCCCC', fontWeight: 700 }}
          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
          labelFormatter={xFormatter}
        />
        <Legend
          verticalAlign="top"
          align="right"
          height={28}
          wrapperStyle={{ paddingBottom: 8 }}
          iconType="circle"
          iconSize={8}
          formatter={(v: string) => (
            <span className="text-xs text-fg-muted">{v}</span>
          )}
        />
        {resolved.map((s) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            stackId="a"
            fill={s.color}
            radius={[0, 0, 0, 0]}
            maxBarSize={28}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
